import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { inputCls, btnPrimary, btnGhost, formatErr } from "@/components/admin/ui";
import { DEFAULT_TEXT, TEXT_GROUPS } from "@/data/siteText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TextManager = ({ token }) => {
    const [fields, setFields] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        axios
            .get(`${API}/site-settings`)
            .then((res) => {
                const stored = res.data.text || {};
                setFields(
                    Object.fromEntries(Object.keys(DEFAULT_TEXT).map((k) => [k, stored[k] ?? DEFAULT_TEXT[k]]))
                );
            })
            .catch(() => setFields({ ...DEFAULT_TEXT }));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/admin/text`, { fields }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Site text updated.");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not save the text."));
        } finally {
            setSaving(false);
        }
    };

    if (fields === null) {
        return <div className="rounded-2xl border border-amber-500/10 bg-zinc-900/40 h-40 animate-pulse" />;
    }

    return (
        <section data-testid="admin-text-tab">
            <div className="space-y-8">
                {TEXT_GROUPS.map((group) => (
                    <div key={group.title} className="rounded-2xl border border-amber-500/20 bg-zinc-900/60 backdrop-blur-md p-5 sm:p-6">
                        <p className="font-meta text-[10px] tracking-[0.3em] uppercase text-amber-400 mb-5">{group.title}</p>
                        <div className="space-y-5">
                            {group.fields.map((field) => (
                                <div key={field.key}>
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <label className="font-meta text-[10px] tracking-[0.2em] uppercase text-zinc-400">
                                            {field.label}
                                        </label>
                                        {fields[field.key] !== DEFAULT_TEXT[field.key] && (
                                            <button
                                                onClick={() => setFields((f) => ({ ...f, [field.key]: DEFAULT_TEXT[field.key] }))}
                                                data-testid={`admin-text-restore-${field.key}`}
                                                className="inline-flex items-center gap-1.5 font-meta text-[10px] tracking-wider uppercase text-zinc-500 hover:text-amber-300 transition-colors duration-200"
                                            >
                                                <RotateCcw size={11} /> Restore original
                                            </button>
                                        )}
                                    </div>
                                    {field.long ? (
                                        <textarea
                                            rows={2}
                                            value={fields[field.key]}
                                            onChange={(e) => setFields((f) => ({ ...f, [field.key]: e.target.value }))}
                                            data-testid={`admin-text-input-${field.key}`}
                                            className={`${inputCls} resize-none`}
                                        />
                                    ) : (
                                        <input
                                            value={fields[field.key]}
                                            onChange={(e) => setFields((f) => ({ ...f, [field.key]: e.target.value }))}
                                            data-testid={`admin-text-input-${field.key}`}
                                            className={inputCls}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-3 mt-6 sticky bottom-4">
                <button onClick={save} disabled={saving} data-testid="admin-text-save" className={btnPrimary}>
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    {saving ? "Saving…" : "Save Text"}
                </button>
                <button
                    onClick={() => setFields({ ...DEFAULT_TEXT })}
                    data-testid="admin-text-restore-all"
                    className={btnGhost}
                >
                    <RotateCcw size={13} /> Restore all originals
                </button>
            </div>
        </section>
    );
};

export default TextManager;
